import { useState, useCallback, useEffect } from 'react';
import alertService from '../services/alertService';

export default function useAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAlerts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await alertService.getAlerts();
      setAlerts(res?.data?.data || []);
    } catch (err) {
      setError(err?._normalized?.message || 'Failed to load alerts');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const createAlert = useCallback(async (data) => {
    try {
      const res = await alertService.createAlert(data);
      const newAlert = res?.data?.data || { ...data, id: Date.now(), created_at: new Date().toISOString(), last_triggered_at: null };
      setAlerts((prev) => [...prev, newAlert]);
      return newAlert;
    } catch (err) {
      throw err;
    }
  }, []);

  const updateAlert = useCallback(async (id, data) => {
    setAlerts((prev) => prev?.map((a) => (a?.id === id ? { ...a, ...data } : a)));
    try {
      await alertService.updateAlert(id, data);
    } catch {
      // optimistic update — revert on hard failure is acceptable UX here
    }
  }, []);

  const deleteAlert = useCallback(async (id) => {
    setAlerts((prev) => prev?.filter((a) => a?.id !== id));
    try {
      await alertService.deleteAlert(id);
    } catch {
      // optimistic delete
    }
  }, []);

  const testAlert = useCallback(async (id) => {
    try {
      await alertService.testAlert(id);
      return true;
    } catch {
      return false;
    }
  }, []);

  return { alerts, isLoading, error, fetchAlerts, createAlert, updateAlert, deleteAlert, testAlert };
}
